import { message, Button, Alert, Row, Col, Form, Radio, Modal } from "antd";

import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { useParams, useNavigate } from "react-router";
import { useAPI } from "../../hooks/useAPI";
import { EventData, TicketData } from "../../models/event";
import { useState, useEffect } from "react";
import { ENDPOINTS } from "../../constants/endpoints.constant";
import TicketForm from "./ticketform";
import { formatAllDatesForSubmit } from "../../utils/DateAndTimeConvertor";
import { updateCall } from "../../constants/apicalls.constant";
import { setSelectedEventList } from "../../store/slices/event.slice";
import TicketTable from "./ticket-table";
import { ROUTES } from "../../constants/routes.constant";
import SectionCard from "../../components/SectionCard";
import { transformImageFieldsToUIDs } from "../../utils/TransfromImageFieldsToUIDs";
import { IconArrowNarrowLeft } from "@tabler/icons-react";
import { isEventUnderAdminReview } from "../../utils/eventEditAccess";
import Loader from "../../components/Loader";
import { isTicketSoldOut, canMarkTicketSoldOut, getTicketSoldQuantity } from "../../utils/ticket";

const AddTicket = () => {
  const event = useSelector((state: RootState) => state.event);
  const organizerCommission = useSelector(
    (state: RootState) => state.user?.organization?.commission,
  );
  const currency = useSelector(
    (state: RootState) => state.event?.currency || "INR",
  );

  const [incomplete, setIncomplete] = useState<string>("");
  const [showInlineForm, setShowInlineForm] = useState(false);
  const [editID, setEditID] = useState("");
  const disabledTime = event?.end_date || "";

  const [form] = Form.useForm();
  const [eventForm] = Form.useForm();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const { loading, error, trigger } = useAPI<{
    status: boolean;
    message: string;
    data: EventData;
  }>(ENDPOINTS.event);

  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);

  useEffect(() => {
    if (!event) return;

    eventForm.setFieldsValue({
      add_gst: event.add_gst ?? false,
      add_convenience_fees: event.add_convenience_fees ?? false,
    });
  }, [event]);

  const handlePasteClick = async () => {
    try {
      const text = await navigator.clipboard.readText();

      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch {
        message.error("Clipboard does not contain valid JSON");
        return;
      }

      if (isTicketData(parsed)) {
        setEditID("");
        const { id, ...ticketData } = parsed as any;
        onSubmit(ticketData, "");
      } else {
        message.error("Invalid Ticket Data");
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to read clipboard");
    }
  };

  const NUMERIC_TICKET_FIELDS = [
    "price",
    "quantity",
    "min_per_transaction",
    "max_per_transaction",
    "min_age",
    "max_age",
  ] as const;

  const coerceTicketNumbers = (obj: Record<string, any>) => {
    const result = { ...obj };

    NUMERIC_TICKET_FIELDS.forEach((field) => {
      if (
        result[field] !== undefined &&
        result[field] !== null &&
        result[field] !== ""
      ) {
        const parsed = Number(result[field]);
        if (!isNaN(parsed)) result[field] = parsed;
      }
    });

    return result;
  };

  const isValidNumber = (val: any) =>
    val !== null && val !== "" && !isNaN(Number(val));

  const isTicketData = (data: unknown): data is TicketData => {
    if (typeof data !== "object" || data === null) return false;

    const obj = data as Record<string, any>;

    return (
      typeof obj.name === "string" &&
      (typeof obj.currency === "string" || obj.currency == null) &&
      isValidNumber(obj.price) &&
      isValidNumber(obj.quantity) &&
      isValidNumber(obj.min_per_transaction) &&
      isValidNumber(obj.max_per_transaction) &&
      (typeof obj.start_date === "string" || obj.start_date == null) &&
      (typeof obj.start_time === "string" || obj.start_time == null) &&
      (typeof obj.end_date === "string" || obj.end_date == null) &&
      (typeof obj.end_time === "string" || obj.end_time == null) &&
      (typeof obj.registration_start_date === "string" ||
        obj.registration_start_date == null) &&
      (typeof obj.registration_start_time === "string" ||
        obj.registration_start_time == null) &&
      (typeof obj.registration_end_date === "string" ||
        obj.registration_end_date == null) &&
      (typeof obj.registration_end_time === "string" ||
        obj.registration_end_time == null)
    );
  };

  const onSubmit = async (values: TicketData, overrideEditID?: string) => {
    const effectiveEditID =
      overrideEditID !== undefined ? overrideEditID : editID;
    if (!id) {
      message.error("Event ID missing");
      return;
    }

    try {
      const formattedValues = coerceTicketNumbers(
        formatAllDatesForSubmit(values) as Record<string, any>,
      );

      const existingTickets = event.tickets || [];
      const ticket = formattedValues as TicketData;

      const updatedTickets: TicketData[] = effectiveEditID
        ? existingTickets.map((t) => {
            if (t.id === effectiveEditID) {
              const quantityDiff = (ticket.quantity || 0) - (t.quantity || 0);
              const increasedQuantity = quantityDiff > 0;
              const newAvailableQuantity = (t.available_quantity || 0) + quantityDiff;
              const isQuantitySoldOut = Number(ticket.quantity) === 0;

              return {
                ...t,
                ...ticket,
                id: effectiveEditID,
                available_quantity: isQuantitySoldOut
                  ? 0
                  : newAvailableQuantity < 0
                    ? 0
                    : newAvailableQuantity,
                ...(isQuantitySoldOut
                  ? {
                      status: "sold_out" as const,
                      is_sold_out: true,
                      mark_sold_out: true,
                      reopen_ticket: false,
                    }
                  : increasedQuantity
                    ? {
                        status: "active" as const,
                        is_sold_out: false,
                        mark_sold_out: false,
                        reopen_ticket: true,
                      }
                    : {}),
              };
            }
            return t;
          })
        : [
            ...existingTickets,
            Number(ticket.quantity) === 0
              ? {
                  ...ticket,
                  available_quantity: 0,
                  status: "sold_out" as const,
                  is_sold_out: true,
                  mark_sold_out: true,
                }
              : ticket,
          ];

      let valueToSend: EventData = {
        ...event,
        tickets: updatedTickets,
      };

      valueToSend = transformImageFieldsToUIDs<EventData>(valueToSend);

      valueToSend.steps_info = Array.from(
        new Set(["ticket_config", ...(valueToSend.steps_info || [])]),
      );

      const res = await trigger(updateCall<EventData>(id, valueToSend));

      if (res?.status) {
        dispatch(setSelectedEventList(res.data));
        message.success(
          effectiveEditID
            ? "Ticket updated successfully"
            : "Ticket added successfully",
        );
      } else {
        message.error(res?.message);
      }
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
    } finally {
      setShowInlineForm(false);
      form.resetFields();
      setEditID("");
    }
  };

  const markTicketSoldOut = (ticketId: string) => {
    if (!id || !ticketId) return;

    Modal.confirm({
      title: "Mark ticket as sold out?",
      content:
        "Available quantity will be set to 0 and this ticket will no longer be available for sale.",
      okText: "Mark Sold Out",
      okButtonProps: { danger: true },
      onOk: async () => {
        const existingTicket = event.tickets?.find((t) => t.id === ticketId);
        if (!existingTicket) {
          message.error("Ticket not found");
          return;
        }

        const soldQty = getTicketSoldQuantity(existingTicket);
        const updatedTickets = (event.tickets || []).map((t) =>
          t.id === ticketId
            ? {
                ...existingTicket,
                mark_sold_out: true,
                is_sold_out: true,
                status: "sold_out" as const,
                available_quantity: 0,
                quantity: soldQty,
              }
            : t,
        );

        let valueToSend: EventData = transformImageFieldsToUIDs({
          ...event,
          tickets: updatedTickets,
        });

        const res = await trigger(updateCall<EventData>(id, valueToSend));

        if (res?.status) {
          dispatch(setSelectedEventList(res.data));
          message.success("Ticket marked as sold out");
          setShowInlineForm(false);
          form.resetFields();
          setEditID("");
        } else {
          message.error(res?.message || "Failed to mark ticket as sold out");
        }
      },
    });
  };


  const onSaveAndNext = async (values: any) => {
    if (!id) {
      message.error("Event ID missing");
      return;
    }

    if (!event?.tickets?.length) {
      setIncomplete("Please add at least one ticket before proceeding.");
      return;
    }

    try {
      let valueToSend: EventData = {
        ...event,
        ...values,
      };

      valueToSend = transformImageFieldsToUIDs<EventData>(valueToSend);

      valueToSend.steps_info = Array.from(
        new Set(["ticket_config", ...(valueToSend.steps_info || [])]),
      );

      const res = await trigger(updateCall<EventData>(id, valueToSend));

      if (res?.status) {
        dispatch(setSelectedEventList(res.data));
        message.success("Settings saved successfully");
        navigate(ROUTES.ADD_EVENT_CUSTOM_FORM.path.replace(":id", id));
      } else {
        message.error(res?.message || "Failed to save settings");
      }
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
    }
  };

  if (error) return null;

  const editingTicket = event.tickets?.find((t) => t.id === editID);
  const soldOut = isTicketSoldOut(editingTicket);
  const soldQty = getTicketSoldQuantity(editingTicket);

  return (
    <Loader spinning={loading}>
      <div className="p-24">
        {!showInlineForm && (
          <div className="w-full max-w-3xl mx-auto">
            <div className="flex items-center gap-24 mb-24">
              <Button
                shape="circle"
                icon={<IconArrowNarrowLeft size={18} />}
                variant="filled"
                color="default"
                className="!border shrink-0"
                onClick={() => navigate(`/add-event-details/${id}`)}
              />
              <div>
                <h2 className="text-[20px] font-semibold text-[#111827] leading-tight">
                  Ticket Configuration
                </h2>
                <p className="text-[13px] text-[#6b7280] mt-2">
                  Set up ticket types, pricing and fee preferences for your
                  event
                </p>
              </div>
            </div>

            {incomplete && (
              <Alert
                message="Action Required"
                description={incomplete}
                type="error"
                showIcon
                style={{ marginBottom: 24 }}
                closable
                onClose={() => setIncomplete("")}
              />
            )}

            <div className="w-full">
              <SectionCard
                title="Ticket Types"
                description="Add and manage ticket categories for your event"
              >
                <TicketTable
                  data={event?.tickets}
                  handlePasteClick={handlePasteClick}
                  setEditEventID={setEditID}
                  event_id={id}
                  eventIndex={0}
                  readOnly={isEventUnderAdminReview(event)}
                />
              </SectionCard>

              {/* ── Section: Fee Settings ── */}
              <fieldset
                disabled={isEventUnderAdminReview(event)}
                style={{ border: "none", padding: 0, margin: 0 }}
              >
                <Form
                  form={eventForm}
                  layout="vertical"
                  onFinish={onSaveAndNext}
                  disabled={isEventUnderAdminReview(event)}
                  initialValues={{
                    add_gst: false,
                    add_convenience_fees: false,
                  }}
                >
                  <SectionCard
                    title="Tax & Fee Settings"
                    description="Configure how GST and organiser charges are applied to ticket prices"
                  >
                    <Row gutter={[24, 16]}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="add_gst"
                          label="GST on Ticket Price"
                          style={{ marginBottom: 0 }}
                        >
                          <Radio.Group disabled={event?.status === 'published'}>
                            <Radio value={false}>Inclusive </Radio>
                            <Radio value={true}>Exclusive </Radio>
                          </Radio.Group>
                        </Form.Item>
                      </Col>

                      <Col xs={24} md={12}>
                        <Form.Item
                          name="add_convenience_fees"
                          label={`Organiser Charges (${organizerCommission ?? event?.commission ?? 0}%)`}
                          style={{ marginBottom: 0 }}
                        >
                          <Radio.Group disabled={event?.status === 'published'}>
                            <Radio value={false}>Paid by me</Radio>
                            <Radio value={true}>Paid by participant</Radio>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                    </Row>
                  </SectionCard>

                  <div className="flex justify-end gap-3 pt-4 pb-8">
                    <Button
                      type="primary"
                      htmlType="submit"
                      disabled={isEventUnderAdminReview(event)}
                    >
                      {event?.status === "draft" ? "Save & Continue" : "Update"}
                    </Button>
                  </div>
                </Form>
              </fieldset>
            </div>
          </div>
        )}

        <TicketForm
          showInlineForm={showInlineForm}
          setShowInlineForm={setShowInlineForm}
          editID={editID}
          setEditID={setEditID}
          onSubmit={onSubmit}
          onMarkSoldOut={() => markTicketSoldOut(editID)}
          showMarkSoldOut={Boolean(
            editID && !soldOut && canMarkTicketSoldOut(event),
          )}
          isSoldOut={soldOut}
          eventType={event?.event_type}
          eventStartDate={event?.start_date}
          eventEndDate={event?.end_date}
          eventStartTime={event?.start_time}
          eventEndTime={event?.end_time}
          disabledTime={disabledTime}
          form={form}
          onCancel={() => {
            form.resetFields();
            setEditID("");
            setShowInlineForm(false);
          }}
          currency={currency}
          soldQty={soldQty}
        />
      </div>
    </Loader>
  );
};

export default AddTicket;
